/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1677628800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Mar 01 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "airtable-source",
        type: "source",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Create Airtable personal access token",
          "depth": 0,
          "id": "create-airtable-personal-access-token"
        }, {
          "value": "Connection configuration",
          "depth": 0,
          "id": "connection-configuration"
        }, {
          "value": "Provide credentials",
          "depth": 1,
          "id": "provide-credentials"
        }, {
          "value": "Test your connection",
          "depth": 0,
          "id": "test-your-connection"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }, {
          "value": "Model setup",
          "depth": 1,
          "id": "model-setup"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      code: "code",
      a: "a",
      strong: "strong",
      img: "img",
      ul: "ul",
      li: "li",
      h3: "h3",
      ol: "ol"
    }, _provideComponents(), props.components), {NoCommonErrors} = _components;
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lets you pull data stored in Airtable and push it to downstream destinations. Airtable models let you query data directly from tables or views associated with tables.\nYou only need ", _jsx(_components.code, {
          children: "read"
        }), " permission since Hightouch won't update or create any data in connected tables or views."]
      }), "\n", _jsx(_components.p, {
        children: "Connecting Hightouch to Airtable requires some setup in both platforms. Specifically, you have to create an Airtable personal access token to configure your connection in Hightouch."
      }), "\n", _jsx(_components.h2, {
        id: "create-airtable-personal-access-token",
        children: "Create Airtable personal access token"
      }), "\n", _jsxs(_components.p, {
        children: ["Navigate to your ", _jsx(_components.a, {
          href: "https://airtable.com/create/tokens",
          children: "Airtable Personal access tokens page"
        }), " and click ", _jsx(_components.strong, {
          children: "Create token"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-airtable-pat.png",
          alt: "Airtable account overview page"
        })
      }), "\n", _jsx(_components.p, {
        children: "Give your token a descriptive name, for example, \"Hightouch Integration Key,\" and grant your token the following scopes:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "data.records:read"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "schema.bases:read"
          })
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "sources/source-airtable-scopes.png",
          alt: "Airtable account overview page"
        })
      }), "\n", _jsx(_components.p, {
        children: "Grant your token access to the bases and workspaces you need Hightouch to access, and then copy your token to use it in Hightouch."
      }), "\n", _jsx(_components.h2, {
        id: "connection-configuration",
        children: "Connection configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["To get started, go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/sources",
          children: [_jsx(_components.strong, {
            children: "Sources"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add source"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Airtable"
        }), " and follow the steps below."]
      }), "\n", _jsx(_components.h3, {
        id: "provide-credentials",
        children: "Provide credentials"
      }), "\n", _jsxs(_components.p, {
        children: ["In Hightouch, enter your Airtable ", _jsx(_components.strong, {
          children: "Personal access token"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "test-your-connection",
        children: "Test your connection"
      }), "\n", _jsxs(_components.p, {
        children: ["When setting up Airtable as a source for the first time, Hightouch checks that you have the correct permissions. Once the test passes, click ", _jsx(_components.strong, {
          children: "Continue"
        }), " to finish setup."]
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.p, {
        children: ["Once your source configuration has passed the necessary validation, your source setup is complete. Next, you can set up ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "models"
        }), " to define which data you want to pull from Airtable."]
      }), "\n", _jsx(_components.h3, {
        id: "model-setup",
        children: "Model setup"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In Hightouch, go to the ", _jsxs(_components.a, {
            href: "https://app.hightouch.com/models",
            children: [_jsx(_components.strong, {
              children: "Models"
            }), " overview page"]
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Add model."
          })]
        }), "\n", _jsx(_components.li, {
          children: "Select the Airtable source you previously created."
        }), "\n", _jsxs(_components.li, {
          children: ["Select the ", _jsx(_components.strong, {
            children: "Base"
          }), " that contains the table you want to sync."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select the ", _jsx(_components.strong, {
            children: "Table"
          }), " you want to sync."]
        }), "\n", _jsxs(_components.li, {
          children: ["Optionally, select a ", _jsx(_components.strong, {
            children: "View"
          }), " you want to sync. If you want to filter columns or specify the order to retrieve data from Airtable, you can create an Airtable view with the fields in the preferred order."]
        }), "\n", _jsx(_components.li, {
          children: "Preview your model's query results."
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Continue"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Name your model and select its ", _jsx(_components.a, {
            href: "/models/creating-models#unique-primary-key-requirement",
            children: _jsx(_components.strong, {
              children: "primary key"
            })
          }), ". Hightouch uses the primary key to determine which rows have been added, changed, or removed since the last sync."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(NoCommonErrors, {
        source: "true"
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
